import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import * as CANNON from 'cannon-es';
import CannonDebugger from 'cannon-es-debugger';
import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';
import CarTest from './CarTest';
import * as Utils from './Utils.js';
import { XRControllerModelFactory } from 'three/addons/webxr/XRControllerModelFactory.js';
import { XRHandModelFactory } from 'three/addons/webxr/XRHandModelFactory.js';
import { Text } from 'troika-three-text';
import AudioManager from './Car_AudioManager.js';

class Enemy {
    constructor(scene, physicsWorld, model, animations, material) {
        this.scene = scene;
        this.physicsWorld = physicsWorld;

        this.mesh = SkeletonUtils.clone(model);
        this.mesh.scale.set(0.5, 0.5, 0.5);
        this.mesh.traverse(child => {
            if (child.isMesh) {
                child.castShadow = true;
                if (child.material) {
                    child.material.roughness = 1;
                }
            }
        });
        this.scene.add(this.mesh);

        const shape = new CANNON.Box(new CANNON.Vec3(0.5, 1, 0.5));
        this.body = new CANNON.Body({
            mass: 2,
            shape: shape,
            fixedRotation: true,
            material: material,
            linearDamping: 0.0
        });

        const enemyHeight = 2; // meters
        const enemyRadius = 0.4; // meters

        // Define the physics properties
        const enemyBodyOptions = {
            mass: 20, // kg
            fixedRotation: true,
            material: material,
            linearDamping: 0.0
        };

        // Create the capsule body using our helper function
        this.body = Utils.createCapsuleCollider(enemyRadius, enemyHeight, enemyBodyOptions);

        this.body.userData = { type: 'enemy', enemy: this };
        this.physicsWorld.addBody(this.body);

        this.mixer = new THREE.AnimationMixer(this.mesh);
        const walkAction = this.mixer.clipAction(
            THREE.AnimationClip.findByName(animations, 'Armature|mixamo.com|Layer0')
        );
        if (walkAction) {
            walkAction.play();
        }

        this.state = 'wandering';
        this.wanderTime = 0;
        this.wanderDirection = new THREE.Vector3();

        // const lineGeometry = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0, 1)]);
        // this.raycastHelper = new THREE.Line(lineGeometry, new THREE.LineBasicMaterial({ color: 0x00ff00 }));
        // this.scene.add(this.raycastHelper);
    }

    update(delta, playerBody) {
        this.mixer.update(delta);

        const distanceToPlayer = this.body.position.distanceTo(playerBody.position);

        if (distanceToPlayer < 10) {
            this.state = 'chasing';
        } else {
            this.state = 'wandering';
        }

        let moveDirection = new THREE.Vector3();

        if (this.state === 'chasing') {
            moveDirection.subVectors(
                new THREE.Vector3().copy(playerBody.position),
                new THREE.Vector3().copy(this.body.position)
            ).normalize();
        } else { // Wandering
            this.wanderTime -= delta;
            if (this.wanderTime <= 0) {
                this.wanderDirection.set(
                    Math.random() * 2 - 1,
                    0,
                    Math.random() * 2 - 1
                ).normalize();
                this.wanderTime = Math.random() * 5 + 2; // New direction every 2-7 seconds
            }
            moveDirection.copy(this.wanderDirection);
        }

        // Obstacle Avoidance
        const eyePosition = new CANNON.Vec3().copy(this.body.position);
        eyePosition.y += 1; // Eye level
        const rayTo = new CANNON.Vec3().copy(this.body.position);
        rayTo.vadd(new CANNON.Vec3(moveDirection.x, 0, moveDirection.z).scale(2), rayTo);

        // this.raycastHelper.geometry.setFromPoints([
        //     new THREE.Vector3(eyePosition.x, eyePosition.y, eyePosition.z),
        //     new THREE.Vector3(rayTo.x, rayTo.y, rayTo.z)
        // ]);

        const ray = new CANNON.Ray(eyePosition, rayTo);
        const result = new CANNON.RaycastResult();
        if (ray.intersectWorld(this.physicsWorld, { result, collisionFilterMask: 1 })) {
            // If we hit something, turn away
            moveDirection.applyAxisAngle(new THREE.Vector3(0, 1, 0), 0);
        } else {
        }


        const speed = 1;
        this.body.velocity.x = moveDirection.x * speed;
        this.body.velocity.z = moveDirection.z * speed;

        if (moveDirection.lengthSq() > 0.1) {
            const angle = Math.atan2(moveDirection.x, moveDirection.z);
            const quaternion = new CANNON.Quaternion();
            quaternion.setFromAxisAngle(new CANNON.Vec3(0, 1, 0), angle);
            this.body.quaternion.slerp(quaternion, 0.1, this.body.quaternion);
        }

        this.mesh.position.copy(this.body.position);
        this.mesh.position.y -= 1; // Adjust for pivot point at feet
        this.mesh.quaternion.copy(this.body.quaternion);
    }
}


class FracturedObject {
    // --- STATIC PROPERTIES ---
    // These are shared across ALL instances of FracturedObject. This is our global pool.
    // Key: A unique name for the fractured model (e.g., its asset path).
    // Value: A Map of { geometryUUID: InstancedMesh }.
    static meshPool = new Map();

    // A single, shared array of all active chunks from all fractured objects.
    static activeChunks = [];
    static inactiveChunks = new Map(); // Pool for reuse. Key: poolKey, Value: array of chunk objects
    static MAX_CHUNKS = 20;
    static bodiesToRemove = []; // For deferred removal

    static _tempVec3 = new THREE.Vector3();
    static _tempQuat = new THREE.Quaternion();
    static _tempMatrix = new THREE.Matrix4();

    // --- STATIC METHOD ---
    // A single update function to be called from the main game loop.
    static update() {

        const updatedMeshes = new Set(); // Use a Set for efficiency

        this.activeChunks.forEach(chunk => {
            if (!chunk.body.world) return; // Skip if body has been removed

            const position = this._tempVec3.copy(chunk.body.position);
            const quaternion = this._tempQuat.copy(chunk.body.quaternion);

            const finalMatrix = this._tempMatrix.compose(
                position,
                quaternion,
                new THREE.Vector3(0.5, 0.5, 0.5) // Default scale
            );

            chunk.mesh.setMatrixAt(chunk.instanceIndex, finalMatrix);
            updatedMeshes.add(chunk.mesh); // Mark this mesh as needing an update
        });

        // Tell Three.js to upload the matrix data for all updated meshes
        updatedMeshes.forEach(mesh => {
            mesh.instanceMatrix.needsUpdate = true;
        });
    }

    // --- NEW: Process deferred body removals ---
    static processRemovals(physicsWorld) {
        while (this.bodiesToRemove.length > 0) {
            physicsWorld.removeBody(this.bodiesToRemove.pop());
        }
    }

    // --- STATIC METHOD for pooling and cleanup ---
    static manageChunkCount() {
        if (this.activeChunks.length > this.MAX_CHUNKS) {
            // Sort by spawn time, oldest first
            this.activeChunks.sort((a, b) => a.spawnTime - b.spawnTime);

            const chunksToDeactivate = this.activeChunks.length - this.MAX_CHUNKS;
            const oldestChunks = this.activeChunks.splice(0, chunksToDeactivate);

            oldestChunks.forEach(chunk => {
                // 1. Queue body for removal from physics world
                if (chunk.body.world) {
                    this.bodiesToRemove.push(chunk.body);
                }

                // 2. Hide the instance by scaling to zero
                const matrix = this._tempMatrix.makeScale(0, 0, 0);
                chunk.mesh.setMatrixAt(chunk.instanceIndex, matrix);

                // 3. Add to the inactive pool for reuse
                const poolKey = chunk.poolKey;
                if (!this.inactiveChunks.has(poolKey)) {
                    this.inactiveChunks.set(poolKey, []);
                }
                this.inactiveChunks.get(poolKey).push(chunk);

                // 4. Make the instance index available again
                chunk.mesh.userData.freeIndices.push(chunk.instanceIndex);
            });
        }
    }


    constructor(scene, physicsWorld, fracturedModel, originalMesh, type, fracturedModelName, impulseVelocity = null) {
        this.scene = scene;
        this.physicsWorld = physicsWorld;
        this.originalMesh = originalMesh;
        this.type = type;
        this.fracturedModelName = fracturedModelName;
        this.impulseVelocity = impulseVelocity;

        this.createFracturedPieces(fracturedModel);
    }

    createFracturedPieces(fracturedModel) {

        if (!FracturedObject.meshPool.has(this.fracturedModelName)) {
            const geometryMap = new Map();
            fracturedModel.traverse((chunk) => {
                if (chunk.isMesh && !geometryMap.has(chunk.geometry.uuid)) {
                    // Create an InstancedMesh with a generous pre-allocated size for pooling.
                    const initialCount = 50; // Increased from 10


                    const instancedMesh = new THREE.InstancedMesh(chunk.geometry, chunk.material, initialCount);
                    instancedMesh.castShadow = true;
                    instancedMesh.frustumCulled = false;
                    this.scene.add(instancedMesh);
                    instancedMesh.userData.usedCount = 0; // Track how many instances are active
                    instancedMesh.userData.freeIndices = []; // For reusing indices
                    geometryMap.set(chunk.geometry.uuid, instancedMesh);
                }
            });
            FracturedObject.meshPool.set(this.fracturedModelName, geometryMap);
        }

        // Retrieve the map of InstancedMeshes for this specific model type.
        const geometryMap = FracturedObject.meshPool.get(this.fracturedModelName);

        // --- CHUNK SPAWNING LOGIC (similar to before) ---
        const fracturedClone = fracturedModel.clone();
        const worldPosition = new THREE.Vector3();
        const worldQuaternion = new THREE.Quaternion();
        const worldScale = new THREE.Vector3();
        this.originalMesh.matrixWorld.decompose(worldPosition, worldQuaternion, worldScale);

        fracturedClone.position.copy(worldPosition);
        fracturedClone.quaternion.copy(worldQuaternion);
        fracturedClone.scale.copy(worldScale);
        fracturedClone.updateMatrixWorld(true);

        fracturedClone.traverse((chunk) => {
            if (chunk.isMesh) {
                const poolKey = `${this.fracturedModelName}_${chunk.geometry.uuid}`;
                const inactivePool = FracturedObject.inactiveChunks.get(poolKey);
                let chunkData;

                // Calculate transform regardless of new or reused
                const finalPosition = new THREE.Vector3().copy(chunk.position).applyMatrix4(fracturedClone.matrixWorld);
                const finalQuaternion = new THREE.Quaternion().multiplyQuaternions(fracturedClone.quaternion, chunk.quaternion);
                const finalScale = worldScale.clone();

                if (inactivePool && inactivePool.length > 0) {
                    // --- REUSE A CHUNK ---
                    chunkData = inactivePool.pop();

                    // Reactivate and reset physics body
                    chunkData.body.position.copy(finalPosition);
                    chunkData.body.quaternion.copy(finalQuaternion);
                    chunkData.body.velocity.set(0, 0, 0);
                    chunkData.body.angularVelocity.set(0, 0, 0);
                    chunkData.body.wakeUp();
                    this.physicsWorld.addBody(chunkData.body);

                } else {
                    // --- CREATE A NEW CHUNK ---
                    const body = this.createChunkBody(chunk, finalPosition, finalQuaternion, finalScale);
                    if (!body) return;

                    const instancedMesh = geometryMap.get(chunk.geometry.uuid);
                    if (!instancedMesh) return;

                    let instanceIndex;
                    if (instancedMesh.userData.freeIndices.length > 0) {
                        instanceIndex = instancedMesh.userData.freeIndices.pop();
                    } else {
                        instanceIndex = instancedMesh.userData.usedCount++;
                    }

                    if (instanceIndex >= instancedMesh.count) {
                        console.error("Exceeded instance count for", this.fracturedModelName, ". Consider increasing initialCount.");
                        // Cleanup the created body
                        this.physicsWorld.removeBody(body);
                        return;
                    }

                    chunkData = {
                        body: body,
                        mesh: instancedMesh,
                        instanceIndex: instanceIndex,
                        poolKey: poolKey,
                    };
                }

                if (this.impulseVelocity) {
                    // Start with a portion of the player's velocity
                    const impulse = new CANNON.Vec3().copy(this.impulseVelocity).scale(0.2); // Use 20%
                    const scatter = new CANNON.Vec3(
                        (Math.random() - 0.5) * 4,
                        Math.random() *4,
                        (Math.random() - 0.5) * 4
                    );

                    // Combine them and apply to the chunk's velocity
                    chunkData.body.velocity.copy(impulse.vadd(scatter));
                }

                // --- COMMON SETUP for new and reused chunks ---
                chunkData.spawnTime = performance.now();
                FracturedObject.activeChunks.push(chunkData);

                // Set its initial transform for rendering
                const matrix = new THREE.Matrix4().compose(finalPosition, finalQuaternion, finalScale);
                chunkData.mesh.setMatrixAt(chunkData.instanceIndex, matrix);
            }
        });

        // After spawning all chunks for this object, manage the total count
        FracturedObject.manageChunkCount();
    }

    createChunkBody(chunk, position, quaternion, scale) {
        // The physics shape size must match the final calculated visual scale
        const chunkShape = this.createShapeFromMesh(chunk, scale);
        if (chunkShape) {
            const body = new CANNON.Body({
                mass: 0.1,
                position: new CANNON.Vec3().copy(position),
                quaternion: new CANNON.Quaternion().copy(quaternion),
            });
            body.addShape(chunkShape);
            this.physicsWorld.addBody(body);
            return body;
        }
        return null;
    }

    createShapeFromMesh(mesh, finalScale) {
        mesh.geometry.computeBoundingBox();
        const { max, min } = mesh.geometry.boundingBox;

        // The physics box size is the geometry's local size multiplied by its FINAL calculated scale.
        // We use the 'finalScale' passed in, not mesh.getWorldScale().
        const boxSize = new THREE.Vector3().subVectors(max, min).multiply(finalScale);

        if (boxSize.x <= 0.001 || boxSize.y <= 0.001 || boxSize.z <= 0.001) return null;

        return new CANNON.Box(new CANNON.Vec3(boxSize.x / 2, boxSize.y / 2, boxSize.z / 2));
    }


}

class BloodParticles {
    constructor(scene, count = 200) { // Increased count for multiple explosions
        this.scene = scene;
        this.count = count;
        this.particles = [];
        this.dummy = new THREE.Object3D();
        this.nextIndex = 0;

        const geometry = new THREE.BoxGeometry(0.1, 0.1, 0.1);
        const material = new THREE.MeshBasicMaterial({ color: 0xff0000 }); // Red color
        this.mesh = new THREE.InstancedMesh(geometry, material, count);
        this.mesh.castShadow = false;
        this.mesh.receiveShadow = false;
        this.mesh.frustumCulled = false;
        this.scene.add(this.mesh);

        for (let i = 0; i < count; i++) {
            this.particles.push({
                position: new THREE.Vector3(),
                velocity: new THREE.Vector3(),
                life: 0,
            });
            // Initially hide all particles
            this.dummy.scale.set(0, 0, 0);
            this.dummy.updateMatrix();
            this.mesh.setMatrixAt(i, this.dummy.matrix);
        }
        this.mesh.instanceMatrix.needsUpdate = true;
    }

    explode(position, impulse) {
        const particleCount = 30; // Number of particles per explosion
        for (let i = 0; i < particleCount; i++) {
            const p = this.particles[this.nextIndex];

            p.position.copy(position);
            p.velocity.set(
                (Math.random() - 0.5) * 7,
                Math.random() * 5 + 7, // Upwards velocity
                (Math.random() - 0.5) * 7
            );
            if (impulse) {
                p.velocity.x += impulse.x;
                p.velocity.y += impulse.y;
                p.velocity.z += impulse.z;
            }
            p.life = 3; // 3 seconds life

            this.nextIndex = (this.nextIndex + 1) % this.count; // Cycle through particles
        }
        console.log('Explosion at:', position);
    }

    update(delta) {
        const gravity = 19.62; // A bit stronger gravity
        let needsUpdate = false;

        for (let i = 0; i < this.count; i++) {
            const p = this.particles[i];
            if (p.life > 0) {
                needsUpdate = true;
                p.life -= delta;

                if (p.life <= 0) {
                    this.dummy.scale.set(0, 0, 0);
                } else {
                    p.velocity.y -= gravity * delta;
                    p.position.add(p.velocity.clone().multiplyScalar(delta));
                    this.dummy.position.copy(p.position);
                    this.dummy.scale.set(1, 1, 1);
                }
                this.dummy.updateMatrix();
                this.mesh.setMatrixAt(i, this.dummy.matrix);
            }
        }

        if (needsUpdate) {
            this.mesh.instanceMatrix.needsUpdate = true;
        }
    }
}

class Carmagedon {
    orbitalCamera;
    playerCamera;
    activeCamera;

    scene;
    renderer;
    controls;
    gameContainer;
    ui2d;
    physicsWorld;
    cannonDebugger;
    mouse = new THREE.Vector2();
    playerBody;
    playerMesh;
    keysPressed = {};
    fracturedModels = {};
    destructibleTypes = {
        'bench': 'assets/carmagedon/bench_blue_fractured.glb',
        'traffic_light': 'assets/carmagedon/taffic_light_fractured.glb',
        'lamp_street': 'assets/carmagedon/lampt_street_fractured.glb',
        'hydrant': 'assets/carmagedon/hydrant_fractured.glb'
    };
    dynamicObjects = [];
    fracturedObjects = [];
    enemies = [];
    enemyModel = null;
    enemyAnims = null;
    fracturedEnemyModel = null;
    groundMaterial = new CANNON.Material('ground');
    enemyMaterial = new CANNON.Material('enemy');
    bloodParticles = null;

    // Enemy spawning properties
    spawnPoints = [];
    minEnemies = 3;
    maxEnemies = 8;
    spawnRadius = 8;
    spawnCooldown = 10; // seconds
    timeSinceLastSpawn = 0;

    car;
    cameraMode = 'orbital';


    playerRig;
    hands = [];
    controllers = [];
    controllerModelFactory = new XRControllerModelFactory();
    handModelFactory = new XRHandModelFactory();

    config = {
        showStats: true,
    }

    lastTime = 0;
    frameTimes = [];
    maxFrameSamples = 60; // Average FPS over the last 60 frames
    audioManager;
    disposed = false;


    COL_GROUP_DEFAULT = 1;
    COL_GROUP_CAR = 2;
    COL_GROUP_GROUND = 4;
    COL_GROUP_DEBRIS = 8;
    constructor({ gameContainer, ui2d, leaderboard }) {
        this.gameContainer = gameContainer;
        this.leaderboard = leaderboard || null;
        this.ui2d = {
            container: ui2d,
            score: ui2d.querySelector('#score-2d'),
            start: ui2d.querySelector('#start-btn-2d'),
            restart: ui2d.querySelector('#restart-btn-2d'),
        };
        this.init();
        this.onResize();
        this.audioManager = new AudioManager();

    }

    onStartClick = () => {
        this.startGame();
    }

    onKeyDown = (event) => {
        this.keysPressed[event.key.toLowerCase()] = true;
        if (event.key.toLowerCase() === 'c') {
            this.switchCameraMode();
        }
    }

    onKeyUp = (event) => {
        this.keysPressed[event.key.toLowerCase()] = false;
    }

    onResize = () => {
        const { clientWidth, clientHeight } = this.gameContainer;
        this.renderer.setSize(clientWidth, clientHeight);
        this.orbitalCamera.aspect = clientWidth / clientHeight;
        this.orbitalCamera.updateProjectionMatrix();
        this.playerCamera.aspect = clientWidth / clientHeight;
        this.playerCamera.updateProjectionMatrix();
    }

    isGameStarted = false;

    startGame() {
        if (this.disposed) return;
        this.audioManager.start();
        this.spawnInitialEnemies();

        this.isGameStarted = true;
        this.ui2d.start.style.display = 'none';

    }
    createStatsUI() {
        if (!this.config.showStats) return;

        // Container for the UI elements
        const statsContainer = new THREE.Group();
        statsContainer.position.set(-0.05, -0.1, -0.5); // Position it top-left
        statsContainer.rotation.x = -Math.PI / 4; // Less aggressive tilt
        this.playerRig.add(statsContainer);

        const textStyle = {
            font: 'assets/duck_hunt/SpaceMono-Bold.ttf',
            fontSize: 0.018,
            color: 0x00ff00, // Green color for stats
            anchorX: 'left',
            anchorY: 'top',
            textAlign: 'left'
        };

        this.statsText = new Text();
        this.statsText.text = 'Initializing...';
        Object.assign(this.statsText, textStyle);
        this.statsText.position.set(0, 0, 0); // Position relative to container
        this.statsText.sync();
        statsContainer.add(this.statsText);
    }

    updateStats() {
        if (!this.statsText) return;

        const totalFrameTime = this.frameTimes.reduce((acc, dt) => acc + dt, 0);
        const averageFrameTime = totalFrameTime / this.frameTimes.length;
        const fps = (1 / averageFrameTime).toFixed(0);

        const triangles = this.renderer.info.render.triangles;
        const drawCalls = this.renderer.info.render.calls;

        this.statsText.text = `FPS: ${fps}\nTriangles:  ${triangles}\nCalls: ${drawCalls}`;
        this.statsText.sync();
    }

    createSkyDome() {
        const skyGeometry = new THREE.SphereGeometry(500, 32, 32);
        const canvas = document.createElement('canvas');
        canvas.width = 512;
        canvas.height = 512;
        const context = canvas.getContext('2d');
        const gradient = context.createLinearGradient(0, 0, 0, canvas.height);
        gradient.addColorStop(0, '#006effff');
        gradient.addColorStop(1, '#00eeffff');
        context.fillStyle = gradient;
        context.fillRect(0, 0, canvas.width, canvas.height);
        const skyTexture = new THREE.CanvasTexture(canvas);
        const skyMaterial = new THREE.MeshBasicMaterial({ map: skyTexture, side: THREE.BackSide, fog: false });
        this.skyDome = new THREE.Mesh(skyGeometry, skyMaterial);
        this.scene.add(this.skyDome);
    }

    async init() {
        this.scene = new THREE.Scene();
        const aspect = window.innerWidth / window.innerHeight;
        this.orbitalCamera = new THREE.PerspectiveCamera(75, aspect, 0.1, 1000);
        this.orbitalCamera.position.set(0, 15, 30);
        this.playerCamera = new THREE.PerspectiveCamera(75, aspect, 0.1, 1000);

        this.activeCamera = this.orbitalCamera; // Start with the orbital camera



        this.renderer = new THREE.WebGLRenderer({ antialias: true });
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setAnimationLoop(this.gameLoop);
        // this.renderer.shadowMap.enabled = true;
        // this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        this.renderer.xr.enabled = true;

        this.renderer.xr.addEventListener('sessionstart', () => {
            this.cameraMode = 'xr';
            this.activeCamera = this.playerCamera; // --- NEW: Switch active camera
            // Parent the player camera to the rig for XR
            this.playerRig.add(this.playerCamera);
            this.playerCamera.position.set(0, 0, 0);
            this.playerCamera.quaternion.identity();
            this.playerRig.scale.set(0.8, 0.8, 0.8);
            this.startGame();
        });

        this.renderer.xr.addEventListener('sessionend', () => {
            // Re-parent camera to the scene for orbital controls.
            this.scene.add(this.playerCamera);
            this.activeCamera = this.orbitalCamera; // --- NEW: Switch active camera

            // When exiting XR, move the orbital camera back to a reasonable default.
            if (this.car.chassisMesh) {
                const offset = new THREE.Vector3(0, 5, -15);
                offset.applyQuaternion(this.car.chassisMesh.quaternion);
                offset.add(this.car.chassisMesh.position);
                this.orbitalCamera.position.copy(offset);
                this.controls.target.copy(this.car.chassisMesh.position);
            }
            this.cameraMode = 'orbital';
            this.controls.enabled = true;
        });

        this.ui2d.start.addEventListener('click', this.onStartClick);

        // You now show the start button initially
        this.ui2d.start.style.display = 'block';
        this.gameContainer.appendChild(this.renderer.domElement);

        this.controls = new OrbitControls(this.orbitalCamera, this.renderer.domElement);
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.25;
        this.controls.maxDistance = 50;

        this.initPhysics();
        this.initScene();
        this.loadModels();
        this.bloodParticles = new BloodParticles(this.scene);
        this.car = new CarTest(this.scene, this.physicsWorld, new THREE.Vector3(0, 2, 0));
        await this.car._init();
        if (this.disposed) return;

        const carBodyMaterial = this.car.chassisBody.material;
        const carGroundContactMaterial = new CANNON.ContactMaterial(
            this.groundMaterial,
            carBodyMaterial,
            {
                friction: 0.05, // Very low friction for sliding against walls
                restitution: 0.1,
            }
        );
        this.physicsWorld.addContactMaterial(carGroundContactMaterial);

        this.initPlayer();
        this.loadEnemyAssetsAndSpawn();

        window.addEventListener('resize', this.onResize);
        window.addEventListener('keydown', this.onKeyDown);


        this.createPlayerRig();
        this.createStatsUI();

        this.setupControls();

        window.addEventListener('keyup', this.onKeyUp);
    }


    setupControls() {
        this.setupXrControls();
    }
    createPlayerRig() {
        this.playerRig = new THREE.Group();
        this.scene.add(this.playerRig);

    }
    switchCameraMode() {
        if (this.cameraMode === 'orbital') {
            this.cameraMode = 'fps';
            this.activeCamera = this.playerCamera; // --- NEW: Switch active camera
        } else {
            this.cameraMode = 'orbital';
            this.activeCamera = this.orbitalCamera; // --- NEW: Switch active camera
            if (this.car.chassisMesh) {
                // Instantly move orbital camera back
                const offset = new THREE.Vector3(0, 5, -15);
                offset.applyQuaternion(this.car.chassisMesh.quaternion);
                offset.add(this.car.chassisMesh.position);
                this.orbitalCamera.position.copy(offset);
                this.controls.target.copy(this.car.chassisMesh.position);
            }
        }
    }
    initPhysics() {
        this.physicsWorld = new CANNON.World({ gravity: new CANNON.Vec3(0, -9.82, 0), allowSleep: true });
        this.cannonDebugger = new CannonDebugger(this.scene, this.physicsWorld, {});
        this.physicsWorld.broadphase = new CANNON.SAPBroadphase(this.physicsWorld);

        const groundEnemyContactMaterial = new CANNON.ContactMaterial(
            this.groundMaterial,
            this.enemyMaterial,
            {
                friction: 0.0,
                restitution: 0.3
            }
        );

        this.physicsWorld.addContactMaterial(groundEnemyContactMaterial);
    }

    initScene() {
        const dLight = new THREE.DirectionalLight('white', 2);
        dLight.position.set(1, 3, 0);
        dLight.castShadow = true;
        dLight.shadow.mapSize.width = 4096;
        dLight.shadow.mapSize.height = 4096;
        const d = 35;
        dLight.shadow.camera.left = -d;
        dLight.shadow.camera.right = d;
        dLight.shadow.camera.top = d;
        dLight.shadow.camera.bottom = -d;
        this.scene.add(dLight);

        const aLight = new THREE.AmbientLight('white', 0.5);
        this.scene.add(aLight);
        this.createSkyDome();
    }

    loadModels() {
        const loader = new GLTFLoader();
        for (const type in this.destructibleTypes) {
            const path = this.destructibleTypes[type];
            loader.load(path, (gltf) => {
                this.fracturedModels[type] = gltf.scene;
                this.fracturedModels[type].traverse((child) => {
                    child.receiveShadow = false;
                });
            });
        }
        loader.load('assets/carmagedon/monster_02_fractured.glb', (gltf) => {
            this.fracturedEnemyModel = gltf.scene;
            //this.fracturedEnemyModel.scale.set(1, 1, 1);
            gltf.scene.traverse((child) => {
                child.receiveShadow = false;
                child.castShadow = true;
            });
        });

        const maxAnisotropy = this.renderer.capabilities.getMaxAnisotropy();
        loader.load('assets/carmagedon/city.glb', (gltf) => {
            const city = gltf.scene;
            this.scene.add(city);
            const colors = ["red", "green", "yellow"];
            let colorIndex = 0;
            city.traverse((child) => {
                if (child.name.startsWith('spawn_point_')) {
                    const spawnPointPosition = new THREE.Vector3();
                    child.getWorldPosition(spawnPointPosition);
                    this.spawnPoints.push(spawnPointPosition);
                    child.visible = false; // Hide the visual representation
                }
                if (child.isMesh) {
                    if (child.name.toLowerCase().includes('house_middle_box')) {
                        const color = new THREE.Color(colors[colorIndex % colors.length]);
                        if (Array.isArray(child.material)) {
                            child.material = child.material.map(material => {
                                const newMaterial = material.clone();
                                newMaterial.color.set(color);
                                return newMaterial;
                            });
                        } else if (child.material) {
                            const newMaterial = child.material.clone();
                            newMaterial.color.set(color);
                            child.material = newMaterial;
                        }
                        colorIndex++;
                    }

                    child.castShadow = false;
                    child.receiveShadow = true;
                    // Apply anisotropy to all maps if they exist
                    if (child.material) {
                        const maps = ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap', 'emissiveMap'];
                        maps.forEach((mapName) => {
                            if (child.material[mapName]) {
                                child.material[mapName].anisotropy = maxAnisotropy;
                                child.material[mapName].needsUpdate = true;
                            }
                        });
                        child.material.side = THREE.FrontSide; // Ensure all materials are front-facing
                    }
                    const body = this.createPhysicsFromCollider(child);
                    for (const type in this.destructibleTypes) {
                        if (child.name.includes(type)) {
                            if (body) body.userData.type = type;
                        }
                    }
                }
            });
        });
    }

    initPlayer() {
        this.playerBody = this.car.chassisBody;
        console.log(this.playerBody);
        this.playerBody.addEventListener('collide', (event) => {
            const otherBody = event.body;
            const type = otherBody.userData?.type;
            if (type && this.destructibleTypes[type] && !otherBody.userData.destroyed) {
                console.log(otherBody.userData);
                otherBody.userData.destroyed = true;
                this.destroyObject(otherBody.userData.mesh, otherBody, type);
            } else if (type === 'enemy' && !otherBody.userData.destroyed) {
                otherBody.userData.destroyed = true;
                const enemy = otherBody.userData.enemy;
                this.destroyEnemy(enemy);
            }
        });
    }

    loadEnemyAssetsAndSpawn() {
        const loader = new GLTFLoader();
        loader.load('assets/carmagedon/monster_02.glb', (gltf) => {
            this.enemyModel = gltf.scene;
            this.enemyModel.castShadow = true;
            this.enemyModel.receiveShadow = false;
            loader.load('assets/carmagedon/monster_02_anim.glb', (animGltf) => {
                this.enemyAnims = animGltf.animations;
            });
        });
    }

    spawnInitialEnemies() {
        if (!this.enemyModel || this.spawnPoints.length === 0) {
            console.warn("Enemy model or spawn points not loaded for initial spawn.");
            return;
        }
        for (let i = 0; i < this.minEnemies; i++) {
            this.spawnSingleEnemy();
        }
    }

    spawnSingleEnemy() {
        if (this.enemies.length >= this.maxEnemies || !this.enemyModel || this.spawnPoints.length === 0) {
            return;
        }

        const spawnPoint = this.spawnPoints[Math.floor(Math.random() * this.spawnPoints.length)];

        const angle = Math.random() * Math.PI * 2;
        const radius = Math.random() * this.spawnRadius;
        const offsetX = Math.cos(angle) * radius;
        const offsetZ = Math.sin(angle) * radius;

        const spawnPosition = new THREE.Vector3(
            spawnPoint.x + offsetX,
            spawnPoint.y +0.5,
            spawnPoint.z + offsetZ
        );

        const enemy = new Enemy(this.scene, this.physicsWorld, this.enemyModel, this.enemyAnims, this.enemyMaterial);
        enemy.body.position.copy(spawnPosition);
        this.enemies.push(enemy);
    }

    updateEnemySpawning(delta) {
        if (!this.isGameStarted || !this.enemyModel || this.spawnPoints.length === 0) {
            return;
        }

        this.timeSinceLastSpawn += delta;
        if (this.enemies.length < this.maxEnemies && this.timeSinceLastSpawn >= this.spawnCooldown) {
            this.spawnSingleEnemy();
            this.timeSinceLastSpawn = 0;
        }

        if (this.enemies.length < this.minEnemies) {
            this.spawnSingleEnemy();
        }
    }

    destroyObject(mesh, body, type) {
        mesh.visible = false;
        this.physicsWorld.removeBody(body);
        const fracturedModel = this.fracturedModels[type];
        const fracturedModelAssetPath = this.destructibleTypes[type];
        if (fracturedModel) {
            new FracturedObject(this.scene, this.physicsWorld, fracturedModel, mesh, type, fracturedModelAssetPath, this.playerBody.velocity);
        }
    }

    destroyEnemy(enemy) {
        const explosionPosition = new THREE.Vector3().copy(enemy.body.position);
        explosionPosition.y -= 1; // Raise the explosion position slightly
        this.bloodParticles.explode(explosionPosition, this.playerBody.velocity);

        enemy.mixer.stopAllAction();
        this.scene.remove(enemy.mesh);
        this.scene.remove(enemy.raycastHelper);
        this.physicsWorld.removeBody(enemy.body);

        const index = this.enemies.indexOf(enemy);
        if (index > -1) {
            this.enemies.splice(index, 1);
        }

        const fracturedModelAssetPath = 'monster02'
        if (this.fracturedEnemyModel) {
            new FracturedObject(this.scene, this.physicsWorld, this.fracturedEnemyModel, enemy.mesh, fracturedModelAssetPath);
        }
    }

    removeEnemy(enemy) {
        enemy.mixer.stopAllAction();
        this.scene.remove(enemy.mesh);
        if (enemy.raycastHelper) {
            this.scene.remove(enemy.raycastHelper);
        }
        this.physicsWorld.removeBody(enemy.body);

        const index = this.enemies.indexOf(enemy);
        if (index > -1) {
            this.enemies.splice(index, 1);
        }
    }

    createPhysicsFromMesh(mesh) {
        mesh.geometry.computeBoundingBox();
        const { max, min } = mesh.geometry.boundingBox;
        const worldPosition = new THREE.Vector3();
        const worldQuaternion = new THREE.Quaternion();
        const worldScale = new THREE.Vector3();
        mesh.matrixWorld.decompose(worldPosition, worldQuaternion, worldScale);
        const boxSize = new THREE.Vector3().subVectors(max, min).multiply(worldScale);
        if (boxSize.x <= 0 || boxSize.y <= 0 || boxSize.z <= 0) return null;
        const shape = new CANNON.Box(new CANNON.Vec3(boxSize.x / 2, boxSize.y / 2, boxSize.z / 2));
        const body = new CANNON.Body({
            mass: 0,
            shape: shape,
            position: new CANNON.Vec3().copy(worldPosition),
            quaternion: new CANNON.Quaternion().copy(worldQuaternion),
            material: this.groundMaterial
        });
        body.userData = { mesh: mesh.parent };
        mesh.userData.body = body;
        this.physicsWorld.addBody(body);
        return body;
    }
    createPhysicsFromCollider(mesh) {
        // --- 1. Gatekeeper: Only process meshes designated as colliders ---
        if (!mesh.name.startsWith('col_')) {
            return null;
        }

        // --- 2. Hide the collider from the final render ---
        mesh.visible = false;

        // --- 3. Determine the required physics shape from the name ---
        let shapeType = null;
        if (mesh.name.includes('_box_')) {
            shapeType = 'box';
        } else if (mesh.name.includes('_sphere_')) {
            shapeType = 'sphere';
        } else if (mesh.name.includes('_plane_')) {
            shapeType = 'plane';
        }

        if (!shapeType) {
            console.warn(`Collider "${mesh.name}" does not specify a valid shape type (e.g., _box_). Skipping.`);
            return null;
        }

        // --- 4. Get the mesh's world transform ---
        // This is the corrected section.
        const worldPosition = new THREE.Vector3();
        const worldQuaternion = new THREE.Quaternion();
        const worldScale = new THREE.Vector3(); // This vector is required by the decompose function.

        // The decompose method REQUIRES all three arguments.
        mesh.matrixWorld.decompose(worldPosition, worldQuaternion, worldScale);


        // --- 5. Create the appropriate CANNON.Shape ---
        let shape;
        switch (shapeType) {
            case 'box': {
                mesh.geometry.computeBoundingBox();
                const size = new THREE.Vector3();
                mesh.geometry.boundingBox.getSize(size);
                // Apply the scale we just extracted to get the final size.
                size.multiply(worldScale);
                const halfExtents = new CANNON.Vec3(size.x / 2, size.y / 2, size.z / 2);
                shape = new CANNON.Box(halfExtents);
                break;
            }

            case 'sphere': {
                mesh.geometry.computeBoundingSphere();
                // We take the largest component of the scale to be safe.
                const radius = mesh.geometry.boundingSphere.radius * Math.max(worldScale.x, worldScale.y, worldScale.z);
                shape = new CANNON.Sphere(radius);
                break;
            }

            case 'plane': {
                shape = new CANNON.Plane();
                break;
            }
        }

        // --- 6. Create the CANNON.Body ---
        const body = new CANNON.Body({
            mass: 0,
            shape: shape,
            position: new CANNON.Vec3().copy(worldPosition),
            quaternion: new CANNON.Quaternion().copy(worldQuaternion),
            material: this.groundMaterial,
        });

        body.userData = { mesh: mesh.parent };
        mesh.userData.body = body;
        // --- 7. Add the body to the physics world and return it ---
        this.physicsWorld.addBody(body);
        return body;
    }


    clock = new THREE.Clock();
    gameLoop = () => {
        const delta = this.clock.getDelta();
        FracturedObject.processRemovals(this.physicsWorld);
        this.physicsWorld.step(1 / 90, delta, 3);

        this.updateEnemySpawning(delta);

        this.frameTimes.push(delta);
        if (this.frameTimes.length > this.maxFrameSamples) {
            this.frameTimes.shift();
        }

        let throttle = 0;
        let steering = 0;
        if (this.cameraMode === 'xr') { // Use cameraMode instead of isPresenting
            const { throttle: xrThrottle, steering: xrSteering } = this.handleXrControls();
            throttle = xrThrottle;
            steering = xrSteering;
        } else {
            // Keyboard controls for both orbital and fps
            if (this.keysPressed['w'] || this.keysPressed['arrowup']) throttle = 1;
            else if (this.keysPressed['s'] || this.keysPressed['arrowdown']) throttle = -1;

            if (this.keysPressed['a'] || this.keysPressed['arrowleft']) steering = -1;
            else if (this.keysPressed['d'] || this.keysPressed['arrowright']) steering = 1;
        }


        if (!this.car || !this.car.chassisMesh)
            return;



        if (this.car) {
            this.car.update(delta, throttle, steering);

            // --- AUDIO UPDATE ---
            const forwardSpeed = this.car.getForwardSpeed();
            const MIN_SPEED_FOR_BRAKE_SOUND = 1.0; // Car must be going this fast to make a brake sound

            // Condition 1: Is the player intentionally hard braking?
            // This is TRUE if pressing reverse while moving forward above the minimum speed.
            const isHardBraking = throttle < 0 && forwardSpeed > MIN_SPEED_FOR_BRAKE_SOUND;

            // Condition 2: Is the car sliding sideways?
            // We get this from our simplified physics function.
            const physicsSlip = this.car.getWheelSlip();

            // Combine the two conditions:
            // If we are hard braking, force the slip value to maximum (1.0) for a guaranteed sound.
            // Otherwise, use the value from the physics simulation for sideways skids.
            const finalSlipValue = isHardBraking ? 0.8 : physicsSlip;

            // Pass the final, reliable slip value to the audio manager.
            // Note we no longer pass isHardBraking separately.
            this.audioManager.update(throttle, forwardSpeed, finalSlipValue);
        }


        switch (this.cameraMode) {
            case 'orbital':
                if (this.car.chassisMesh) {
                    this.controls.target.copy(this.car.chassisMesh.position);
                }
                this.controls.update(); // Only update controls for this camera
                break;
            case 'fps':
                if (this.car.chassisMesh && this.car.seats.length > 0) {
                    const driverSeat = this.car.seats[0].seatPointObject;
                    const seatPosition = new THREE.Vector3();
                    driverSeat.getWorldPosition(seatPosition);
                    seatPosition.y += 0.5;
                    this.playerCamera.position.copy(seatPosition); // Update playerCamera

                    const targetPosition = new THREE.Vector3(0, 0, 10);
                    this.car.chassisMesh.localToWorld(targetPosition);
                    this.playerCamera.lookAt(targetPosition); // Update playerCamera
                }
            case 'xr':
                if (this.car.seats.length > 0) {
                    const driverSeat = this.car.seats[0].seatPointObject;
                    const seatPosition = new THREE.Vector3();
                    const seatQuaternion = new THREE.Quaternion();
                    driverSeat.getWorldPosition(seatPosition);
                    driverSeat.getWorldQuaternion(seatQuaternion);

                    const rotationY = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI);
                    const finalQuaternion = seatQuaternion.multiply(rotationY);

                    seatPosition.y += 0.5;
                    this.playerRig.position.copy(seatPosition);
                    this.playerRig.quaternion.copy(finalQuaternion);
                }
                break;
        }


        for (let i = this.enemies.length - 1; i >= 0; i--) {
            const enemy = this.enemies[i];
            enemy.update(delta, this.playerBody);
            if (enemy.body.position.y < -10) {
                this.removeEnemy(enemy);
            }
        }

        FracturedObject.update();

        this.bloodParticles.update(delta);

        if (this.config.showStats) {
            this.updateStats();
        }
        //this.cannonDebugger.update();
        this.renderer.render(this.scene, this.activeCamera);
    }


    //XR
    handleXrControls() {
        let throttle = 0;
        let steering = 0;
        const session = this.renderer.xr.getSession();
        if (!session || !session.inputSources) return { throttle, steering };

        for (const source of session.inputSources) {
            if (source.gamepad) {
                if (source.handedness === 'right') {
                    if (source.gamepad.buttons[0] && source.gamepad.buttons[0].pressed) {
                        throttle = source.gamepad.buttons[0].value;
                    }

                }
                if (source.handedness === 'left') {
                    if (source.gamepad.buttons[0] && source.gamepad.buttons[0].pressed) {
                        throttle = -source.gamepad.buttons[0].value;
                    }
                    if (source.gamepad.axes.length > 2) {
                        steering = source.gamepad.axes[2];
                    }
                }
            }
        }
        return { throttle, steering };
    }
    setupXrControls() {
        if (this.disposed) return;
        for (let i = 0; i < 2; i++) {
            // Controllers
            const controller = this.renderer.xr.getController(i);
            this.playerRig.add(controller);
            this.controllers.push(controller);


            controller.addEventListener('selectstart', () => {
                // When the trigger is pressed, set our flag to true
                controller.userData.isGrabbing = true;
            });

            controller.addEventListener('selectend', () => {
                // When the trigger is released, set our flag to false
                controller.userData.isGrabbing = false;
            });
            // Controller Models
            const grip = this.renderer.xr.getControllerGrip(i);
            grip.add(this.controllerModelFactory.createControllerModel(grip));
            this.playerRig.add(grip);

            // Hand Models
            const hand = this.renderer.xr.getHand(i);
            hand.add(this.handModelFactory.createHandModel(hand, 'mesh'));
            this.playerRig.add(hand);
            this.hands.push(hand);
        }
    }

    dispose = () => {
        this.disposed = true;
        if (this.renderer) this.renderer.setAnimationLoop(null);

        window.removeEventListener('resize', this.onResize);
        window.removeEventListener('keydown', this.onKeyDown);
        window.removeEventListener('keyup', this.onKeyUp);
        if (this.ui2d?.start) this.ui2d.start.removeEventListener('click', this.onStartClick);

        if (this.audioManager && typeof this.audioManager.dispose === 'function') {
            this.audioManager.dispose();
        }

        if (this.controls && typeof this.controls.dispose === 'function') {
            this.controls.dispose();
        }
        if (this.statsText) this.statsText.dispose();

        if (this.scene) {
            this.scene.traverse((object) => {
                if (object.geometry) object.geometry.dispose();
                if (object.material) {
                    if (Array.isArray(object.material)) {
                        object.material.forEach((material) => material.dispose());
                    } else {
                        object.material.dispose();
                    }
                }
            });
        }
        if (this.renderer) this.renderer.dispose();
    }
}
export default Carmagedon;